import { useCallback, useEffect, useState } from "react";

import toast from "react-hot-toast";
import { useTranslation } from "react-i18next";

import { differenceInDays, isValid, subDays } from "date-fns";
import { CheckIcon, PlusIcon, SearchIcon } from "lucide-react";

import { formatDate } from "simplydo/core/dates";
import { useThrottle } from "simplydo/react/hooks";
import { styled } from "simplydo/react/styled";
import { useTheme } from "simplydo/react/theme";
import { Button } from "simplydo/react/ui/button";
import { Checkbox } from "simplydo/react/ui/checkbox";
import { DateInput } from "simplydo/react/ui/date";
import { Divider } from "simplydo/react/ui/divider";
import {
  Dropdown,
  DropdownHeader,
  DropdownItem,
  DropdownMenu,
} from "simplydo/react/ui/dropdown";
import { FormField, FormLabel } from "simplydo/react/ui/form";
import { H3, H4, Subheader } from "simplydo/react/ui/header";
import { Icon } from "simplydo/react/ui/icon";
import { Input } from "simplydo/react/ui/input";
import { Label } from "simplydo/react/ui/label";
import {
  Modal,
  ModalActions,
  ModalContent,
  ModalHeader,
} from "simplydo/react/ui/modal";
import { Popup } from "simplydo/react/ui/popup";
import {
  Table,
  TableBody,
  TableHeader,
  TableHeaderCell,
} from "simplydo/react/ui/table";
import { TextArea } from "simplydo/react/ui/textarea";

import api from "api";
import { useCurrentUser } from "utils/currentUser";
import { sdiClickDomain } from "utils/domains";
import util from "utils/utils";

import { PaginationWithLimiter } from "components/lib/UI";

import type {
  ClickDateKey,
  LineChartData,
  LinkChartDataFn,
  LinkSortKey,
  LinksCustomDates,
  LinksSearchState,
  ShortenedLink,
  ShortenedUrlClicksMap,
  ShortenedUrlSearchItem,
  ShortenedUrlSearchResults,
  ShortenedUrlsSort,
} from "../types";
import { toShortenedUrlsQuery } from "../types";
import LinkRow from "./LinkRow";
import { DefaultCustomDates } from "./Links.constants";

const QueryContainer = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
`;

const SortableTableRow = styled.tr`
  .not-sortable {
    pointer-events: none;
    cursor: default;
  }
`;

const ShowingClicksFilter = styled.div`
  display: flex;
  flex-direction: column;
  margin-top: 20px;
  margin-bottom: 20px;
  > h5 {
    margin: 5px 0;
    display: block;
  }
  > .filters {
    gap: 8px;
    margin-top: 5px;
    display: flex;
    flex-direction: row;
    align-items: center;
    .custom-date-chooser {
      margin-left: 10px;
      display: flex;
      flex-direction: row;
      align-items: center;
    }
  }
`;

const SortTypes: Record<LinkSortKey, ShortenedUrlsSort[]> = {
  path: ["pathAsc", "pathDesc"],
  createdAt: ["createdAtNewest", "createdAtOldest"],
  clicks: ["clicksAsc", "clicksDesc"],
};

const ClickDateOptions: Record<
  ClickDateKey,
  { startDay: number; endDay: number }
> = {
  last30days: { startDay: 30, endDay: 0 },
  last60days: { startDay: 60, endDay: 0 },
  last90days: { startDay: 90, endDay: 0 },
  custom: { startDay: 30, endDay: 0 },
};

type ShortLinkCreatorProps = {
  description: string;
  setDescription: (description: string) => void;
  url: string;
  setUrl: (url: string) => void;
  path: string;
  setPath: (path: string) => void;
};

export const ShortLinkCreator = ({
  description,
  setDescription,
  url,
  setUrl,
  path,
  setPath,
}: ShortLinkCreatorProps) => {
  const [searchResults, setSearchResults] = useState<
    ShortenedUrlSearchResults | undefined
  >(undefined);
  const [searchTerm, setSearchTerm] = useState("");
  const user = useCurrentUser();
  const orgId = user?.ownerOrganisation?._id;

  const { t } = useTranslation();

  const fetchSearchResults = useThrottle(
    (term: string) => {
      if (!orgId) return;

      api.organisations.searchPotentialShortenedUrls(
        orgId,
        term,
        (data) => {
          setSearchResults(data.searchResults);
        },
        () => toast.error("Unable to search at this time"),
      );
    },
    400,
    [orgId],
  );

  const updateSearchTerm = useCallback(
    (term: string) => {
      setSearchTerm(term);
      fetchSearchResults(term);
    },
    [fetchSearchResults],
  );

  const setLinkData = useCallback(
    (item: ShortenedUrlSearchItem) => {
      setDescription(item.name);
      setUrl(item.link);
      setSearchTerm("");
      setSearchResults(undefined);
    },
    [setDescription, setUrl],
  );

  const renderSearchSection = (
    title: string,
    items: ShortenedUrlSearchItem[] | undefined,
  ) => {
    if (!items?.length) return null;

    return (
      <>
        <DropdownHeader>{title}</DropdownHeader>
        {items.slice(0, 5).map((item) => (
          <DropdownItem
            key={`${title}-${item.name}-${item.link}`}
            onClick={() => setLinkData(item)}
          >
            {item.name}
          </DropdownItem>
        ))}
      </>
    );
  };

  return (
    <>
      <p>
        To create a link directly to any {t("generic.challenge")}, group, poll
        or idea you can search for it below.
      </p>
      <Dropdown
        trigger={
          <Input
            value={searchTerm}

            iconPosition="left"
            placeholder="Search by name..."
            onChange={(e) => updateSearchTerm(e.target.value)}
          />
        }
      >
        <DropdownMenu>
          {searchResults ? (
            <>
              {renderSearchSection(
                t("common:capitalise", { key: "generic.challenges" }),
                searchResults.challenges,
              )}
              {renderSearchSection(
                t("common:capitalise", { key: "generic.groups" }),
                searchResults.groups,
              )}
              {renderSearchSection("Polls", searchResults.polls)}
              {renderSearchSection(
                t("common:capitalise", { key: "generic.ideas" }),
                searchResults.ideas,
              )}
            </>
          ) : null}
        </DropdownMenu>
      </Dropdown>
      <Divider hidden />
      <FormField style={{ maxWidth: 600 }}>
        <FormLabel htmlFor="shortenUrlLabel">
          URL to shorten (e.g. to {t("generic.challengeWithArticle")})
        </FormLabel>
        <Input
          id="shortenUrlLabel"
          placeholder="https://..."
          value={url}
          onChange={(e) => setUrl(e.target.value)}
        />
      </FormField>
      <FormField style={{ maxWidth: 600 }}>
        <FormLabel htmlFor="shortUrlLabel">
          Short URL (maximum of 30 characters)
        </FormLabel>
        <Input
          id="shortUrlLabel"
          label={<Label>{sdiClickDomain}/</Label>}
          placeholder="myurl"
          value={path}
          onChange={(e) => setPath(e.target.value)}
        />
      </FormField>
      <FormField style={{ maxWidth: 600 }}>
        <FormLabel htmlFor="shortenDescriptionLabel">
          Description - <i>optional</i>
        </FormLabel>
        <TextArea
          id="shortenDescriptionLabel"
          value={description}
          onChange={(_e, { value }) => {
            setDescription(value);
          }}
        />
      </FormField>
    </>
  );
};

const Links = () => {
  const user = useCurrentUser();
  const orgId = user?.ownerOrganisation?._id;
  const [url, setUrl] = useState("");
  const [path, setPath] = useState("");
  const [description, setDescription] = useState("");
  const [links, setLinks] = useState<ShortenedLink[]>([]);
  const [total, setTotal] = useState(0);
  const [linkModalOpen, setLinkModalOpen] = useState(false);

  const { t } = useTranslation();

  const [searchState, setSearchState] = useState<LinksSearchState>({
    startDay: 30,
    endDay: 0,
    query: "",
    sort: "",
    limit: 15,
    page: 1,
    groupByUrl: false,
  });
  const [clickDates, setClickDates] = useState<ClickDateKey>("last30days");
  const [customDates, setCustomDates] =
    useState<LinksCustomDates>(DefaultCustomDates);
  const [clicks, setClicks] = useState<ShortenedUrlClicksMap>({});
  const theme = useTheme();

  const canChangeSettings = util.hasPermission(
    user,
    "org.changeSettings",
    orgId,
  );

  const getShortenedUrls = useCallback(() => {
    if (!orgId) return;

    // We need to check customDates are valid Date objects
    if (
      !isValid(new Date(searchState.startDay)) ||
      !isValid(new Date(searchState.endDay))
    ) {
      toast.error("Please ensure the dates you entered are valid");
      return;
    }
    api.organisations.getShortenedUrls(
      orgId,
      toShortenedUrlsQuery(searchState),
      (data) => {
        setLinks(data.shortenedUrls);
        setTotal(data.total);
        const clickData = data.clicks;
        setClicks(
          typeof clickData === "object" && clickData !== null
            ? (clickData as ShortenedUrlClicksMap)
            : {},
        );
      },
      () => {
        toast.error("Unable to load links at this time");
      },
    );
  }, [orgId, searchState]);

  useEffect(() => {
    getShortenedUrls();
  }, [getShortenedUrls]);

  useEffect(() => {
    setSearchState((prevSearchState) => ({
      ...prevSearchState,
      ...ClickDateOptions[clickDates],
    }));
    setCustomDates(DefaultCustomDates);
  }, [clickDates]);

  const generateDatasets = useCallback((label: string) => {
    const colour = "#fae7bd";
    return {
      borderColor: util.adjustColour(colour, -0.05),
      pointBackgroundColor: util.adjustColour(colour, -0.05),
      backgroundColor: colour,
      data: [] as number[],
      label,
      pointRadius: 4,
    };
  }, []);

  const chartData: LinkChartDataFn = useCallback(
    (forLink) => {
      const labels: string[] = [];
      const dataset = generateDatasets(forLink.path);
      const linkClicks = clicks[forLink._id];
      if (linkClicks) {
        Object.keys(linkClicks).forEach((d) => {
          labels.push(d);
        });
        Object.values(linkClicks).forEach((v) => {
          dataset.data.push(v);
        });
      }
      return { labels, datasets: [dataset] };
    },
    [generateDatasets, clicks],
  );

  const createUrl = useCallback(() => {
    if (!orgId) return;

    api.organisations.createShortenedUrl(
      orgId,
      { url, path, description },
      (shortenedUrl) => {
        setUrl("");
        setPath("");
        setDescription("");
        setLinkModalOpen(false);
        setLinks((prevLinks) => [shortenedUrl, ...prevLinks]);
      },
      (err) => toast.error(err.message),
    );
  }, [orgId, url, path, description]);

  const deleteUrl = useCallback(
    (urlId: string) => {
      if (!orgId) return;

      util
        .confirm(
          "Really delete this URL?",
          "The URL and associated analytics data will be immediately removed. Be careful - someone else can take your short URL once you delete it.",
        )
        .then(() => {
          api.organisations.deleteShortenedUrl(
            orgId,
            urlId,
            () => {
              setLinks((prevLinks) =>
                prevLinks.filter((link) => link._id !== urlId),
              );
            },
            (err) => toast.error(err.message),
          );
        })
        .catch(() => {});
    },
    [orgId],
  );

  const copyLink = useCallback((link: string) => {
    void util.copyToClipboard(`${sdiClickDomain}/${link}`);
  }, []);

  const getDirection = useCallback(
    (sortType: LinkSortKey): "ascending" | "descending" | undefined => {
      if (searchState.sort.indexOf(sortType) === -1) return undefined;
      if (
        searchState.sort.indexOf("Newest") > -1 ||
        searchState.sort.indexOf("Asc") > -1
      )
        return "ascending";
      if (
        searchState.sort.indexOf("Oldest") > -1 ||
        searchState.sort.indexOf("Desc") > -1
      )
        return "descending";
      return undefined;
    },
    [searchState.sort],
  );

  const handleSort = useCallback(
    (sortType: LinkSortKey) => {
      const sortTypeOptions = SortTypes[sortType];
      const currentSort = sortTypeOptions.indexOf(
        searchState.sort as (typeof sortTypeOptions)[number],
      );
      if (currentSort === -1 || currentSort === sortTypeOptions.length - 1) {
        setSearchState((prevState: LinksSearchState) => ({
          ...prevState,
          sort: "",
        }));
      } else {
        const nextSort = sortTypeOptions[currentSort + 1];
        if (nextSort) {
          setSearchState((prevState: LinksSearchState) => ({
            ...prevState,
            sort: nextSort,
          }));
        }
      }
    },
    [searchState.sort],
  );

  const updateClicksDates = useCallback(
    (dateKey: keyof LinksCustomDates, newDate: string) => {
      const newDateItem = new Date(newDate);
      setCustomDates((prevCustomDates) => ({
        ...prevCustomDates,
        [dateKey]: newDateItem,
      }));
    },
    [],
  );

  useEffect(() => {
    if (clickDates === "custom") {
      const currentDate = new Date();
      setSearchState((prevSearchState) => ({
        ...prevSearchState,
        startDay: Math.abs(
          differenceInDays(new Date(customDates.startDay), currentDate),
        ),
        endDay: Math.abs(
          differenceInDays(new Date(customDates.endDay), currentDate),
        ),
      }));
    }
  }, [clickDates, customDates]);

  return (
    <>
      <H3>
        Shortened links
        <Subheader>
          {util.appName()} allows you to create custom links that directly go to{" "}
          {t("generic.challenges")} or {t("generic.ideas")} (or somewhere else)
          using a shorter and easier-to-read URL. Once created, you can use the
          generated links in social media posts, emails, and on web-pages.
        </Subheader>
      </H3>

      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <QueryContainer>
          <Input
            onChange={(e) =>
              setSearchState((prevState) => ({
                ...prevState,
                query: e.target.value,
              }))
            }
            icon={<Icon icon={SearchIcon} />}
            value={searchState.query}
            placeholder="Filter links..."

            iconPosition="left"
            style={{ marginRight: 8 }}
          />
          <Popup
            on="hover"
            hoverable
            trigger={
              <Checkbox
                checked={searchState.groupByUrl}
                onClick={() =>
                  setSearchState((prevState) => ({
                    ...prevState,
                    groupByUrl: !prevState.groupByUrl,
                  }))
                }
              >
                Group links by URL
              </Checkbox>
            }
          >
            {"If checked, sorting is applied on a URL group basis"}
          </Popup>
        </QueryContainer>
        {canChangeSettings ? (
          <Button
            size="small"
            onClick={() => setLinkModalOpen(true)}
            color="primary"
          >
            <Icon icon={PlusIcon} />
            Create a new short link
          </Button>
        ) : null}
      </div>
      <ShowingClicksFilter>
        <p style={{ marginBottom: 0, fontWeight: 600 }}>Show clicks over...</p>
        <div className="filters">
          <Checkbox
            checked={clickDates === "last30days"}
            onClick={() => setClickDates("last30days")}
          >
            Last 30 days
          </Checkbox>
          <Checkbox
            checked={clickDates === "last60days"}
            onClick={() => setClickDates("last60days")}
          >
            Last 60 days
          </Checkbox>
          <Checkbox
            checked={clickDates === "last90days"}
            onClick={() => setClickDates("last90days")}
          >
            Last 90 days
          </Checkbox>
          <Checkbox
            checked={clickDates === "custom"}
            onClick={() => setClickDates("custom")}
          >
            Custom
          </Checkbox>
          {clickDates === "custom" ? (
            <div className="custom-date-chooser">
              <DateInput
                name="date"
                placeholder="From"
                value={formatDate(customDates.startDay, "yyyy-MM-dd")}
                dateFormat="yyyy-MM-dd"
                onChange={(_e, { value }) =>
                  updateClicksDates("startDay", value ?? "")
                }
              />
              {" to "}
              <DateInput
                name="date"
                placeholder="To"
                value={formatDate(customDates.endDay, "yyyy-MM-dd")}
                dateFormat="yyyy-MM-dd"
                onChange={(_e, { value }) =>
                  updateClicksDates("endDay", value ?? "")
                }
              />
            </div>
          ) : null}
        </div>
      </ShowingClicksFilter>
      <Table sortable>
        {!theme.sizes.isMobile && (
          <TableHeader>
            <SortableTableRow>
              <TableHeaderCell
                sorted={getDirection("path")}
                onClick={() => handleSort("path")}
              >
                Link
              </TableHeaderCell>
              <TableHeaderCell
                sorted={getDirection("createdAt")}
                onClick={() => handleSort("createdAt")}
              >
                Created
              </TableHeaderCell>
              <TableHeaderCell
                sorted={getDirection("clicks")}
                onClick={() => handleSort("clicks")}
              >
                Total clicks
              </TableHeaderCell>
              <TableHeaderCell className="not-sortable">
                Clicks (Last 30 days)
              </TableHeaderCell>
              <TableHeaderCell className="not-sortable" />
            </SortableTableRow>
          </TableHeader>
        )}
        <TableBody>
          {links.map((link) => (
            <LinkRow
              key={link._id}
              link={link}
              deleteUrl={deleteUrl}
              copyLink={copyLink}
              chartData={chartData}
            />
          ))}
        </TableBody>
      </Table>
      <PaginationWithLimiter
        limit={searchState.limit}
        page={searchState.page}
        total={total}
        text="links"
        updateLimit={(value) =>
          setSearchState((prevState) => ({ ...prevState, limit: value }))
        }
        updatePage={(value) =>
          setSearchState((prevState) => ({ ...prevState, page: value }))
        }
      />
      <Modal open={linkModalOpen} onClose={() => setLinkModalOpen(false)}>
        <ModalHeader>Create a new link</ModalHeader>
        <ModalContent>
          <ShortLinkCreator
            description={description}
            setDescription={setDescription}
            url={url}
            setUrl={setUrl}
            path={path}
            setPath={setPath}
          />
        </ModalContent>
        <ModalActions>
          <Button onClick={() => setLinkModalOpen(false)}>Cancel</Button>
          <Button onClick={createUrl} color="primary">
            <Icon icon={CheckIcon} />
            Create
          </Button>
        </ModalActions>
      </Modal>
    </>
  );
};

export default Links;
